import React, {
  ReactElement, useEffect, useRef, useState,
} from 'react';
import { useLocation } from 'react-router';
import { Message, waitForElement } from 'slovlex-idsk-react-components';
import { Link, Loader } from 'idsk-react-components';
import { useClient } from '../../../../context/ClientStatusContext';
import { getPopularnyObsahList } from '../../../../client/popularnyObsahClient';
import './popularnyObsah.scss';

const PopularnyObsah = (): ReactElement => {
  const {
    data: popularnyObsahList,
    status: popularnyObsahListStatus,
  } = useClient(() => getPopularnyObsahList());
  const location = useLocation();
  const elementTopPositionRef = useRef<number>(0);
  const [displayError, setDisplayError] = useState(true);

  useEffect(() => {
    // SLASLX-3400 (#vybrane-predpisy): the home page has multiple Loader-
    // wrapped sections (NajnovsiObsah's 3 calls, Aktuality, PopularnyObsah,
    // VybranePredpisy) that each finish at different times. A single
    // debounced scroll lands wrong: when the target's class enters the DOM
    // its rect.top reflects the layout AT THAT MOMENT, but slow APIs above
    // (NajnovsiObsah's Vyhlasene/Mpk/Vyjadrenia can take seconds) keep
    // expanding their sections later and push the target further down. The
    // user lands at roughly a quarter of the final page height.
    //
    // Strategy: re-post scroll-to on every ResizeObserver tick (parent's
    // smooth scroll re-targets on each call), continuing until 2s of layout
    // stability OR a 15s hard cap. We only post when rect.top changed, so
    // we don't spam identical messages.
    if (!location.hash) {
      return undefined;
    }
    const elementClass = location.hash.substring(1);
    const elementSelector = `.${CSS.escape(elementClass)}`;
    const FINISH_STABILITY_MS = 2000;
    const MAX_TOTAL_MS = 15000;
    let cancelled = false;
    let finishTimer: ReturnType<typeof setTimeout> | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let lastPostedTop = Number.NaN;
    let finished = false;

    const finish = (element: Element): void => {
      if (finished || cancelled) return;
      finished = true;
      postIfChanged(element);
      resizeObserver?.disconnect();
      if (finishTimer) {
        clearTimeout(finishTimer);
        finishTimer = null;
      }
      // SLASLX-3400: deliberately NOT clearing window.location.hash here.
      // Clearing it triggers a hashchange → iframe-to-parent /
      // → parent navigate(/) → portal-main's hash-sync effect re-fires
      // → notify iframe / → ... infinite ping-pong between parent and iframe.
      // Trade-off: URL keeps /#X after scroll, but a feedback loop is far
      // worse UX than a slightly verbose URL. Clicking the SAME anchor a
      // second time won't re-scroll (URL didn't change), but clicking a
      // different anchor with a different fragment still works.
    };

    const postIfChanged = (element: Element): void => {
      const rect = element.getBoundingClientRect();
      const top = window.scrollY + rect.top;
      if (Math.round(top) === Math.round(lastPostedTop)) return;
      lastPostedTop = top;
      elementTopPositionRef.current = top;
      window.parent.postMessage({
        type: 'scroll-to',
        top,
      }, '*');
    };

    waitForElement(elementSelector).then((element) => {
      if (cancelled || !element) return;
      const startTime = Date.now();

      const armFinishTimer = (): void => {
        if (finishTimer) clearTimeout(finishTimer);
        finishTimer = setTimeout(() => finish(element), FINISH_STABILITY_MS);
      };

      resizeObserver = new ResizeObserver(() => {
        if (cancelled || finished) return;
        postIfChanged(element);
        if (Date.now() - startTime >= MAX_TOTAL_MS) {
          finish(element);
        } else {
          armFinishTimer();
        }
      });
      resizeObserver.observe(document.documentElement);
      // Initial post + arm: covers the case where no resizes occur after the
      // target appears (e.g. all other sections already finished loading).
      postIfChanged(element);
      armFinishTimer();
    });
    return () => {
      cancelled = true;
      if (finishTimer) clearTimeout(finishTimer);
      resizeObserver?.disconnect();
    };
  }, [location.hash]);

  return (
    <Loader loading={popularnyObsahListStatus?.loading}>
      <div className="popularny-obsah">
        <p>NAJVYHĽADÁVANEJŠÍ OBSAH</p>
        {popularnyObsahListStatus.responseCode && popularnyObsahListStatus.responseCode > 399 ? (
          <>
            {displayError && (
              <Message
                type="error"
                onClear={() => setDisplayError(false)}
                headingSize="m"
              >
                Nepodarilo sa načítať najvyhľadávanejší obsah.
              </Message>
            )}
          </>
        ) : (
          <>
            {popularnyObsahList && (
              <ul>
                {popularnyObsahList.map((popularnyObsah) => (
                  <li key={popularnyObsah.id}>
                    <Link to={popularnyObsah.odkaz}>
                      {popularnyObsah.nazovOdkazu}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Loader>
  );
};

export default PopularnyObsah;
